import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useReservaFilters } from '../../hooks/useReservaFilters';
import ReservaFilters from '../../components/ReservaFilters';
import BotonRuta from '../../components/shared/BotonRuta';
import { getEstadoColor, getSubEstadoColor } from '../../constants/reservaColors';
import { createReservaIcs, downloadIcsFile } from '../../utils/ics';
import {
  Box,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Tooltip,
  VStack,
  HStack,
  IconButton,
  useToast,
  Checkbox,
} from '@chakra-ui/react';

const AdminReservas = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const [reservasOriginal, setReservasOriginal] = useState([]);
  const [reservas, setReservas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('faltan');
  const [sortOrder, setSortOrder] = useState('asc');
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [refreshCountdown, setRefreshCountdown] = useState(60);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [origenesActivos, setOrigenesActivos] = useState({
    web: true,
    chat: true,
    telegram: true,
    whatsapp: true,
  });

  const handleDescargarIcs = (reserva) => {
    const icsData = createReservaIcs(reserva);
    if (!icsData) {
      toast({
        title: 'No se puede generar el calendario',
        description: 'La reserva no tiene fecha de inicio válida.',
        status: 'warning',
        duration: 3500,
        isClosable: true,
      });
      return;
    }

    downloadIcsFile(icsData.content, icsData.filename);
  };
  
  // Hook personalizado para manejo de filtros
  const {
    estadosActivos,
    subEstadosActivos,
    toggleEstado,
    toggleSubEstado
  } = useReservaFilters(
    {
      'activo': true,
      'inactivo': false,
      'cancelado': false
    },
    {
      'bolsa': true,
      'asignada_espera': true,
      'en_curso': true,
      'completado_sin_confirmar': true,
      'pendiente_devolucion': true,
      'pendiente_pago': false,
      'finalizado': false,
      'fallido': false,
      'cancelado_cliente': false,
      'cancelado_administrador': false,
      'cancelado_automatico': false
    }
  );

  const toggleOrigen = (origen) => {
    setOrigenesActivos((prev) => ({
      ...prev,
      [origen]: !prev[origen],
    }));
  };


  // Actualizar reloj cada minuto para la columna "Faltan"
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Actualizar cada minuto

    return () => clearInterval(interval);
  }, []);

  const columnsToDisplay = [
    'acciones',
    'created_at',
    'intervienen',
    'faltan',
    'importe_reserva',
    'importe_adjudicacion',
    'tipo',
    'flow_type',
    'sub_estado',
    'estado',
    'transaccion_reserva',
    'transaccion_adjudicacion',
  ];

  // Función para cancelar reserva
  const handleCancelarReserva = async (reserva) => {
    if (!window.confirm(t('admin_reservas_cancelar_confirmar', { id: reserva.id }))) {
      return;
    }

    // Solicitar motivo de cancelación (en español)
    const motivo = window.prompt(
      t('admin_reservas_cancelar_motivo'),
      ''
    );

    if (!motivo || motivo.trim() === '') {
      alert(t('admin_reservas_cancelar_sin_motivo'));
      return;
    }

    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem('auth-token');
      
      const response = await fetch(`${baseURL}/api/reservas/${reserva.id}/cancelar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          motivo: motivo.trim()
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error al cancelar reserva:', errorData);
        throw new Error(`Error al cancelar la reserva: ${JSON.stringify(errorData)}`);
      }

      // Actualizar la lista localmente
      const reservasActualizadas = reservasOriginal.map(r => 
        r.id === reserva.id 
          ? { ...r, estado: 'cancelado', sub_estado: 'cancelado_cliente' }
          : r
      );
      setReservasOriginal(reservasActualizadas);
      alert(t('admin_reservas_cancelar_exitoso'));
    } catch (error) {
      alert(t('admin_reservas_cancelar_error', { mensaje: error.message }));
    }
  };

  // Función para procesar devolución
  const handleDevolucion = async (reserva, tipo) => {
    // tipo puede ser "reserva" o "adjudicacion"
    const transaccionId = tipo === "reserva" 
      ? reserva.orderid_reserva 
      : reserva.orderid_adjudicacion;
    
    const importe = tipo === "reserva" 
      ? reserva.importe_reserva 
      : reserva.importe_adjudicacion;

    if (!transaccionId || transaccionId === 'N/A') {
      toast({
        title: 'Sin transacción',
        description: `No hay código de transacción para ${tipo === "reserva" ? "la reserva" : "la adjudicación"}`,
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!importe || importe <= 0) {
      toast({
        title: 'Importe inválido',
        description: `El importe de ${tipo === "reserva" ? "la reserva" : "la adjudicación"} debe ser mayor a 0`,
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const confirmar = window.confirm(
      `¿Estás seguro de que quieres devolver ${importe.toFixed(2)}€ de la ${tipo === "reserva" ? "reserva" : "adjudicación"}?\n\n` +
      `Reserva: ${reserva.id}\n` +
      `Transacción: ${transaccionId}\n` +
      `Importe: ${importe.toFixed(2)}€`
    );

    if (!confirmar) return;

    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem('auth-token');
      
      toast({
        title: 'Procesando devolución...',
        description: 'Por favor espera',
        status: 'info',
        duration: 2000,
        isClosable: true,
      });

      const response = await fetch(`${baseURL}/api/pagos/devolucion/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reserva_id: reserva.id,
          transaccion_id: transaccionId,
          importe: importe,
          tipo: tipo
        })
      });
      
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Error al procesar la devolución');
      }

      if (data.success) {
        toast({
          title: '✅ Devolución exitosa',
          description: `Se devolvieron ${importe.toFixed(2)}€. Código de respuesta: ${data.response_code}`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        // Construir mensaje de error con detalles adicionales
        let errorDescription = data.message || 'La devolución fue rechazada por Redsys';
        
        // Si hay una nota adicional (como limitaciones de TEST), añadirla
        if (data.note) {
          errorDescription += `\n\n${data.note}`;
        }
        
        // Si hay un código de error, añadirlo
        if (data.error_code) {
          errorDescription += `\n\nCódigo: ${data.error_code}`;
        }
        
        toast({
          title: '❌ Devolución rechazada',
          description: errorDescription,
          status: 'warning',
          duration: 10000, // Mostrar más tiempo para que pueda leer la nota completa
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error en devolución:', error);
      toast({
        title: 'Error',
        description: error.message || 'Error al procesar la devolución',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Función para cambiar estado de completado_sin_confirmar a pendiente_devolucion
  const handleCambiarAPendienteDevolucion = async (reserva) => {
    if (reserva.sub_estado !== 'completado_sin_confirmar') {
      toast({
        title: 'Estado no válido',
        description: 'Solo se pueden cambiar reservas en estado "Completado sin confirmar"',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const confirmar = window.confirm(
      `¿Estás seguro de que quieres cambiar la reserva ${reserva.id} a "Pendiente de devolución"?\n\n` +
      `Esto cambiará el estado de "Completado sin confirmar" a "Pendiente de devolución".`
    );

    if (!confirmar) return;

    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem('auth-token');
      
      toast({
        title: 'Actualizando estado...',
        description: 'Por favor espera',
        status: 'info',
        duration: 2000,
        isClosable: true,
      });

      const response = await fetch(`${baseURL}/api/reservas/${reserva.id}/cambiar-subestado`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          nuevo_subestado: 'pendiente_devolucion'
        })
      });
      
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Error al cambiar el estado');
      }

      toast({
        title: '✅ Estado actualizado',
        description: 'La reserva ha sido cambiada a "Pendiente de devolución"',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Recargar reservas
      fetchReservas(true);
    } catch (error) {
      console.error('Error al cambiar estado:', error);
      toast({
        title: 'Error',
        description: error.message || 'Error al cambiar el estado',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Función para cambiar a finalizado (estado inactivo, sub_estado finalizado)
  const handleCambiarAFinalizado = async (reserva) => {
    if (reserva.sub_estado !== 'completado_sin_confirmar') {
      toast({
        title: 'Estado no válido',
        description: 'Solo se pueden cambiar reservas en estado "Completado sin confirmar"',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const confirmar = window.confirm(
      `¿Estás seguro de que quieres cambiar la reserva ${reserva.id} a "Finalizado"?\n\n` +
      `Esto cambiará el estado a "Inactivo" y sub-estado a "Finalizado".`
    );

    if (!confirmar) return;

    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem('auth-token');
      
      toast({
        title: 'Actualizando estado...',
        description: 'Por favor espera',
        status: 'info',
        duration: 2000,
        isClosable: true,
      });

      const response = await fetch(`${baseURL}/api/reservas/${reserva.id}/cambiar-estado-completo`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          nuevo_estado: 'inactivo',
          nuevo_subestado: 'finalizado'
        })
      });
      
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Error al cambiar el estado');
      }

      toast({
        title: '✅ Estado actualizado',
        description: 'La reserva ha sido cambiada a "Finalizado"',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Recargar reservas
      fetchReservas(true);
    } catch (error) {
      console.error('Error al cambiar estado:', error);
      toast({
        title: 'Error',
        description: error.message || 'Error al cambiar el estado',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const fetchReservas = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem('auth-token');
      
      const response = await fetch(`${baseURL}/api/reservas/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          `Error fetching reservas: ${response.status} - ${
            errorData?.detail || response.statusText
          }`
        );
      }
      const data = await response.json();
      console.log('[AdminReservas] Primera reserva recibida:', data[0]);
      console.log('[AdminReservas] Campos cliente:', {
        cliente_email: data[0]?.cliente_email,
        cliente_forma_pago: data[0]?.cliente_forma_pago,
        cliente_nombre: data[0]?.cliente_nombre,
        cliente_apellidos: data[0]?.cliente_apellidos
      });
      console.log('[AdminReservas] Campos conductor:', {
        conductor_email: data[0]?.conductor_email,
        conductor_telefono: data[0]?.conductor_telefono,
        conductor_nombre: data[0]?.conductor_nombre,
        conductor_apellidos: data[0]?.conductor_apellidos
      });
      setReservasOriginal(data);
      setReservas(data);
      setLoading(false);
      setIsRefreshing(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchReservas();
  }, []);

  // Auto-refresco cada 60 segundos con contador
  useEffect(() => {
    if (!autoRefreshEnabled) return;

    const interval = setInterval(() => {
      setRefreshCountdown(prev => {
        if (prev <= 1) {
          // Cuando llega a 0, refrescar y resetear
          fetchReservas(true);
          return 60;
        }
        return prev - 1;
      });
    }, 1000); // Actualizar cada segundo

    return () => clearInterval(interval);
  }, [autoRefreshEnabled]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  useEffect(() => {
    // Aplicar filtros primero
    let reservasFiltradas = reservasOriginal.filter(reserva => {
      const estadoActivo = estadosActivos[reserva.estado];
      const subEstadoActivo = subEstadosActivos[reserva.sub_estado];
      const originType = String(reserva.flow_type || 'web').toLowerCase();
      const origenActivo = Object.prototype.hasOwnProperty.call(origenesActivos, originType)
        ? origenesActivos[originType]
        : true;
      return estadoActivo && subEstadoActivo && origenActivo;
    });

    // Luego aplicar ordenamiento
    if (sortBy) {
      const sortedReservas = [...reservasFiltradas].sort((a, b) => {
        // Manejo especial para la columna "faltan"
        if (sortBy === 'faltan') {
          const fechaInicioA = a.fecha_hora_inicio ? new Date(a.fecha_hora_inicio) : null;
          const fechaInicioB = b.fecha_hora_inicio ? new Date(b.fecha_hora_inicio) : null;
          
          if (!fechaInicioA && !fechaInicioB) return 0;
          if (!fechaInicioA) return 1;
          if (!fechaInicioB) return -1;
          
          const diffA = fechaInicioA - currentTime;
          const diffB = fechaInicioB - currentTime;
          
          return sortOrder === 'asc' ? diffA - diffB : diffB - diffA;
        }
        
        const valueA = a[sortBy];
        const valueB = b[sortBy];

        if (typeof valueA === 'string' && typeof valueB === 'string') {
          return sortOrder === 'asc'
            ? valueA.localeCompare(valueB)
            : valueB.localeCompare(valueA);
        } else if (typeof valueA === 'number' && typeof valueB === 'number') {
          return sortOrder === 'asc' ? valueA - valueB : valueB - valueA;
        } else if (typeof valueA === 'boolean' && typeof valueB === 'boolean') {
          return sortOrder === 'asc'
            ? (valueA === valueB ? 0 : (valueA ? 1 : -1))
            : (valueA === valueB ? 0 : (valueA ? -1 : 1));
        } else {
          const stringA = String(valueA);
          const stringB = String(valueB);
          return sortOrder === 'asc'
            ? stringA.localeCompare(stringB)
            : stringB.localeCompare(stringA);
        }
      });
      setReservas(sortedReservas);
    } else {
      setReservas(reservasFiltradas);
    }
  }, [sortBy, sortOrder, reservasOriginal, estadosActivos, subEstadosActivos, origenesActivos, currentTime]);

  const renderCellValue = (reserva, key) => {
    const value = reserva[key];
    
    // Renderizar columna de acciones (cancelar + devoluciones)
    if (key === 'acciones') {
      const yaCancelado = reserva.estado === 'inactivo' || 
                          reserva.sub_estado === 'cancelado_cliente' || 
                          reserva.sub_estado === 'cancelado_administrador' ||
                          reserva.sub_estado === 'cancelado_automatico';
      
      // Datos de devolución de reserva
      const orderidReserva = reserva.orderid_reserva || 'N/A';
      const codigoReserva = reserva.codigo_autorizacion_reserva || 'N/A';
      const importeReserva = reserva.importe_reserva || 0;
      const isDisabledReserva = codigoReserva === 'N/A' || orderidReserva === 'N/A' || !importeReserva || importeReserva <= 0;
      
      // Datos de devolución de adjudicación
      const orderidAdjudicacion = reserva.orderid_adjudicacion || 'N/A';
      const codigoAdjudicacion = reserva.codigo_autorizacion_adjudicacion || 'N/A';
      const importeAdjudicacion = reserva.importe_adjudicacion || 0;
      const isDisabledAdjudicacion = codigoAdjudicacion === 'N/A' || orderidAdjudicacion === 'N/A' || !importeAdjudicacion || importeAdjudicacion <= 0;
      
      // Datos para el tooltip de ruta
      const precioReserva = reserva.precio || 0;
      const distanciaKm = reserva.distancia_km || 0;
      const origenDir = reserva.origen_direccion || reserva.origen_codigo_postal || 'N/A';
      const destinoDir = reserva.destino_direccion || reserva.destino_codigo_postal || 'N/A';
      
      // Formatear fechas
      const formatFecha = (fecha) => {
        if (!fecha) return 'N/A';
        try {
          const date = new Date(fecha);
          return date.toLocaleString('es-ES', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          });
        } catch {
          return 'N/A';
        }
      };
      
      const fechaInicio = formatFecha(reserva.fecha_hora_inicio);
      const fechaFin = formatFecha(reserva.fecha_hora_fin);
      
      // Verificar si tiene datos válidos de ruta para el tooltip
      const tieneRuta = reserva.origen && reserva.destino;
      
      const tooltipRuta = tieneRuta 
        ? (
            <Box className="tooltip-content">
              <Text className="tooltip-title">🗺️ Ver Ruta en Google Maps</Text>
              <Box className="tooltip-fields">
                <Box className="tooltip-field">
                  <Text className="tooltip-field-label">Origen:</Text>
                  <Text className="tooltip-field-value">{origenDir}</Text>
                </Box>
                <Box className="tooltip-field">
                  <Text className="tooltip-field-label">Fecha inicio:</Text>
                  <Text className="tooltip-field-value">{fechaInicio}</Text>
                </Box>
                <Box className="tooltip-field">
                  <Text className="tooltip-field-label">Destino:</Text>
                  <Text className="tooltip-field-value">{destinoDir}</Text>
                </Box>
                <Box className="tooltip-field">
                  <Text className="tooltip-field-label">Fecha fin:</Text>
                  <Text className="tooltip-field-value">{fechaFin}</Text>
                </Box>
                <Box className="tooltip-field">
                  <Text className="tooltip-field-label">Precio aprox.:</Text>
                  <Text className="tooltip-field-value">{precioReserva.toFixed(2)}€</Text>
                </Box>
                <Box className="tooltip-field">
                  <Text className="tooltip-field-label">Distancia:</Text>
                  <Text className="tooltip-field-value">{distanciaKm.toFixed(2)} km</Text>
                </Box>
              </Box>
            </Box>
          )
        : <Text className="tooltip-simple">Sin datos de ruta</Text>;
      
      return (
        <HStack spacing={1} align="stretch">
          {/* Columna izquierda: Mapa y Cancelar */}
          <VStack spacing={1} align="stretch" flex={1}>
            <Tooltip label={tooltipRuta} placement="left" hasArrow borderRadius="0.75rem" bg="transparent">
              <Box>
                <BotonRuta
                  origen={reserva.origen}
                  destino={reserva.destino}
                  size="sm"
                  fullWidth={true}
                  colorScheme="teal"
                  variant="outline"
                />
              </Box>
            </Tooltip>

            <Tooltip label={<Text className="tooltip-simple">📅 Añadir a calendario</Text>} placement="left" hasArrow borderRadius="0.75rem" bg="transparent">
              <Box>
                <Button
                  size="sm"
                  width="full"
                  className="boton-ver-datos"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDescargarIcs(reserva);
                  }}
                  isDisabled={!reserva.fecha_hora_inicio}
                >
                  📅
                </Button>
              </Box>
            </Tooltip>
            
            <Tooltip label={<Text className="tooltip-simple">❌ Cancelar reserva</Text>} placement="left" hasArrow borderRadius="0.75rem" bg="transparent">
              <Box>
                <Button
                  size="sm"
                  width="full"
                  className="boton-cancelar-devolver"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancelarReserva(reserva);
                  }}
                  isDisabled={yaCancelado}
                >
                  ❌
                </Button>
              </Box>
            </Tooltip>
          </VStack>
          
          {/* Columna central: Devoluciones */}
          <VStack spacing={1} align="stretch" flex={1}>
            <Tooltip 
              label={
                isDisabledReserva 
                  ? <Text className="tooltip-simple">⚠️ Sin datos para devolver</Text>
                  : <Box className="tooltip-content">
                      <Text className="tooltip-title">🔄 Devolver Pago Cliente</Text>
                      <Box className="tooltip-fields">
                        <Box className="tooltip-field">
                          <Text className="tooltip-field-label">Importe:</Text>
                          <Text className="tooltip-field-value">{importeReserva.toFixed(2)}€</Text>
                        </Box>
                      </Box>
                    </Box>
              } 
              placement="left"
              hasArrow
              borderRadius="0.75rem"
              bg="transparent"
            >
              <Box>
                <IconButton
                  size="sm"
                  width="full"
                  className="boton-finalizado"
                  icon={<Text>🔄 👤</Text>}
                  isDisabled={isDisabledReserva}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDevolucion(reserva, 'reserva');
                  }}
                  aria-label="Devolver pago de reserva al cliente"
                />
              </Box>
            </Tooltip>
            
            <Tooltip 
              label={
                isDisabledAdjudicacion 
                  ? <Text className="tooltip-simple">⚠️ Sin datos para devolver</Text>
                  : <Box className="tooltip-content">
                      <Text className="tooltip-title">🔄 Devolver Pago Conductor</Text>
                      <Box className="tooltip-fields">
                        <Box className="tooltip-field">
                          <Text className="tooltip-field-label">Importe:</Text>
                          <Text className="tooltip-field-value">{importeAdjudicacion.toFixed(2)}€</Text>
                        </Box>
                      </Box>
                    </Box>
              } 
              placement="left"
              hasArrow
              borderRadius="0.75rem"
              bg="transparent"
            >
              <Box>
                <IconButton
                  size="sm"
                  width="full"
                  className="boton-cancelar-devolver"
                  icon={<Text>🔄 🚕</Text>}
                  isDisabled={isDisabledAdjudicacion}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDevolucion(reserva, 'adjudicacion');
                  }}
                  aria-label="Devolver pago de adjudicación al conductor"
                />
              </Box>
            </Tooltip>
          </VStack>
          
          {/* Columna derecha: Cambiar estado */}
          <VStack spacing={1} align="stretch" flex={1}>
            <Tooltip 
              label={
                reserva.sub_estado === 'completado_sin_confirmar'
                  ? <Box className="tooltip-content">
                      <Text className="tooltip-title">💳 Cambiar a Pendiente de Devolución</Text>
                      <Text className="tooltip-field-value">Cambiar estado a "Pendiente de devolución"</Text>
                    </Box>
                  : <Text className="tooltip-simple">⚠️ Solo para estado "Completado sin confirmar"</Text>
              }
              placement="left"
              hasArrow
              borderRadius="0.75rem"
              bg="transparent"
            >
              <Box>
                <IconButton
                  size="sm"
                  width="full"
                  className="boton-pendiente-devolucion"
                  icon={<Text>💳</Text>}
                  isDisabled={reserva.sub_estado !== 'completado_sin_confirmar'}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCambiarAPendienteDevolucion(reserva);
                  }}
                  aria-label="Cambiar a pendiente de devolución"
                />
              </Box>
            </Tooltip>

            <Tooltip 
              label={
                reserva.sub_estado === 'completado_sin_confirmar'
                  ? <Box className="tooltip-content">
                      <Text className="tooltip-title">✅ Cambiar a Finalizado</Text>
                      <Text className="tooltip-field-value">Cambiar estado a "Inactivo" y sub-estado a "Finalizado"</Text>
                    </Box>
                  : <Text className="tooltip-simple">⚠️ Solo para estado "Completado sin confirmar"</Text>
              }
              placement="left"
              hasArrow
              borderRadius="0.75rem"
              bg="transparent"
            >
              <Box>
                <IconButton
                  size="sm"
                  width="full"
                  className="boton-finalizado"
                  icon={<Text>✅</Text>}
                  isDisabled={reserva.sub_estado !== 'completado_sin_confirmar'}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCambiarAFinalizado(reserva);
                  }}
                  aria-label="Cambiar a finalizado"
                />
              </Box>
            </Tooltip>
          </VStack>
        </HStack>
      );
    }
    
    // Renderizar columna intervienen (idioma + cliente + conductor apilados)
    if (key === 'intervienen') {
      // Renderizar idioma
      const idioma = reserva.idioma ? reserva.idioma.toLowerCase() : 'es';
      const iconosDisponibles = ['es', 'en', 'de', 'fr', 'it', 'ru', 'ca'];
      const iconoPath = iconosDisponibles.includes(idioma) 
        ? `/assets/icons/${idioma}.png` 
        : '/assets/icons/es.png';
      
      return (
        <VStack spacing={2} align="stretch">
          {/* Idioma */}
          <Box display="flex" justifyContent="center" alignItems="center">
            <img 
              src={iconoPath} 
              alt={idioma} 
              style={{ width: '24px', height: '24px', objectFit: 'contain' }}
            />
          </Box>
          
          {/* Cliente */}
          <Tooltip
            label={
              <Box className="tooltip-content">
                <Text className="tooltip-title">Datos del Cliente</Text>
                <Box className="tooltip-fields">
                  <Box className="tooltip-field">
                    <Text className="tooltip-field-label">Nombre:</Text>
                    <Text className="tooltip-field-value">{reserva.cliente_nombre || 'N/A'} {reserva.cliente_apellidos || ''}</Text>
                  </Box>
                  <Box className="tooltip-field">
                    <Text className="tooltip-field-label">Email:</Text>
                    <Text className="tooltip-field-value">{reserva.cliente_email || 'N/A'}</Text>
                  </Box>
                  <Box className="tooltip-field">
                    <Text className="tooltip-field-label">Teléfono:</Text>
                    <Text className="tooltip-field-value">{reserva.cliente_telefono || 'N/A'}</Text>
                  </Box>
                  <Box className="tooltip-field">
                    <Text className="tooltip-field-label">Forma de pago:</Text>
                    <Text className="tooltip-field-value">{reserva.cliente_forma_pago || 'N/A'}</Text>
                  </Box>
                  <Box className="tooltip-field">
                    <Text className="tooltip-field-label">ID:</Text>
                    <Text className="tooltip-field-value">{reserva.cliente_id?.substring(0, 8)}...</Text>
                  </Box>
                </Box>
              </Box>
            }
            placement="right"
            hasArrow
            borderRadius="0.75rem"
            bg="transparent"
          >
            <Button
              size="sm"
              colorScheme="blue"
              className="boton-ver-datos"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/admin/editar-cliente/${reserva.cliente_id}?mode=read`);
              }}
            >
              👁️ 👤
            </Button>
          </Tooltip>
          
          {/* Conductor */}
          {reserva.conductor_id ? (
            <Tooltip
              label={
                <Box className="tooltip-content">
                  <Text className="tooltip-title">Datos del Conductor</Text>
                  <Box className="tooltip-fields">
                    <Box className="tooltip-field">
                      <Text className="tooltip-field-label">Nombre:</Text>
                      <Text className="tooltip-field-value">{reserva.conductor_nombre || 'N/A'} {reserva.conductor_apellidos || ''}</Text>
                    </Box>
                    <Box className="tooltip-field">
                      <Text className="tooltip-field-label">Email:</Text>
                      <Text className="tooltip-field-value">{reserva.conductor_email || 'N/A'}</Text>
                    </Box>
                    <Box className="tooltip-field">
                      <Text className="tooltip-field-label">Teléfono:</Text>
                      <Text className="tooltip-field-value">{reserva.conductor_telefono || 'N/A'}</Text>
                    </Box>
                    <Box className="tooltip-field">
                      <Text className="tooltip-field-label">ID:</Text>
                      <Text className="tooltip-field-value">{reserva.conductor_id?.substring(0, 8)}...</Text>
                    </Box>
                  </Box>
                </Box>
              }
              placement="right"
              hasArrow
              borderRadius="0.75rem"
              bg="transparent"
            >
              <Button
                size="sm"
                colorScheme="teal"
                className="boton-ver-datos"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/admin/editar-conductor/${reserva.conductor_id}?mode=read`);
                }}
              >
                👁️ 🚕
              </Button>
            </Tooltip>
          ) : (
            <Text fontSize="sm" color="gray.500">N/A</Text>
          )}
        </VStack>
      );
    }

    // Renderizar columna faltan (tiempo hasta fecha_hora_inicio)
    if (key === 'faltan') {
      if (!reserva.fecha_hora_inicio) {
        return <Text fontSize="sm" color="gray.500">N/A</Text>;
      }

      const fechaInicio = new Date(reserva.fecha_hora_inicio);
      const ahora = currentTime;
      const diffMs = fechaInicio - ahora;
      const diffMinutes = Math.floor(diffMs / 60000);

      if (diffMinutes < 0) {
        return <Text fontSize="sm" color="red.500" fontWeight="bold">Iniciada</Text>;
      }

      const horas = Math.floor(diffMinutes / 60);
      const minutos = diffMinutes % 60;

      let colorScheme = 'green';
      if (diffMinutes < 60) {
        colorScheme = 'red';
      } else if (diffMinutes < 180) {
        colorScheme = 'orange';
      }

      return (
        <Badge colorScheme={colorScheme} fontSize="0.9em" px={3} py={1} borderRadius="md">
          {horas}h {minutos}m
        </Badge>
      );
    }
    
    // Renderizar botón de ver cliente con email y tooltip con datos completos
    if (key === 'cliente') {
      return (
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            size="sm"
            colorScheme="blue"
            className="boton-ver-datos"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/admin/editar-cliente/${reserva.cliente_id}?mode=read`);
            }}
          >
            👁️
          </Button>
          <Tooltip
            label={
              <Box p={2} fontSize="xs" border="2px solid white" borderRadius="md">
                <Text fontWeight="bold" mb={2}>Datos del Cliente</Text>
                <VStack align="start" spacing={1}>
                  <HStack>
                    <Text fontWeight="semibold">Nombre:</Text>
                    <Text>{reserva.cliente_nombre || 'N/A'} {reserva.cliente_apellidos || ''}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="semibold">Email:</Text>
                    <Text>{reserva.cliente_email || 'N/A'}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="semibold">Teléfono:</Text>
                    <Text>{reserva.cliente_telefono || 'N/A'}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="semibold">Forma de pago:</Text>
                    <Text>{reserva.cliente_forma_pago || 'N/A'}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="semibold">ID:</Text>
                    <Text>{reserva.cliente_id?.substring(0, 8)}...</Text>
                  </HStack>
                </VStack>
              </Box>
            }
            placement="right"
            hasArrow
            bg="gray.800"
            color="white"
          >
            <Text fontSize="sm" cursor="pointer">{reserva.cliente_email || 'N/A'}</Text>
          </Tooltip>
        </Box>
      );
    }

    // Renderizar botón de ver conductor con email y tooltip con datos completos
    if (key === 'conductor_id') {
      if (!value) {
        return <Text fontSize="sm">N/A</Text>;
      }
      const conductorEmail = reserva.conductor_email || 'Sin email';
      return (
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            size="sm"
            colorScheme="teal"
            className="boton-ver-datos"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/admin/editar-conductor/${value}?mode=read`);
            }}
          >
            👁️
          </Button>
          <Tooltip
            label={
              <Box p={2} fontSize="xs" border="2px solid white" borderRadius="md">
                <Text fontWeight="bold" mb={2}>Datos del Conductor</Text>
                <VStack align="start" spacing={1}>
                  <HStack>
                    <Text fontWeight="semibold">Nombre:</Text>
                    <Text>{reserva.conductor_nombre || 'N/A'} {reserva.conductor_apellidos || ''}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="semibold">Email:</Text>
                    <Text>{reserva.conductor_email || 'N/A'}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="semibold">Teléfono:</Text>
                    <Text>{reserva.conductor_telefono || 'N/A'}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="semibold">ID:</Text>
                    <Text>{value?.substring(0, 8)}...</Text>
                  </HStack>
                </VStack>
              </Box>
            }
            placement="right"
            hasArrow
            bg="gray.800"
            color="white"
          >
            <Text fontSize="sm" cursor="pointer">{conductorEmail}</Text>
          </Tooltip>
        </Box>
      );
    }

    // Renderizar origen_direccion con fallback a codigo_postal
    if (key === 'origen_direccion') {
      const direccion = reserva.origen_direccion || reserva.origen_codigo_postal || 'N/A';
      return <Text fontSize="sm">{direccion}</Text>;
    }

    // Renderizar destino_direccion con fallback a codigo_postal
    if (key === 'destino_direccion') {
      const direccion = reserva.destino_direccion || reserva.destino_codigo_postal || 'N/A';
      return <Text fontSize="sm">{direccion}</Text>;
    }

    // Renderizar transaccion_reserva (Order ID + Código Autorización)
    if (key === 'transaccion_reserva') {
      const orderid = reserva.orderid_reserva || 'N/A';
      const codigo = reserva.codigo_autorizacion_reserva || 'N/A';
      
      return (
        <VStack spacing={1} align="stretch">
          <Text fontSize="xs" fontFamily="mono" color="gray.600">Order: {orderid}</Text>
          <Text fontSize="xs" fontFamily="mono" color="gray.600">Auth: {codigo}</Text>
        </VStack>
      );
    }

    // Renderizar importe_reserva con formato de moneda
    if (key === 'importe_reserva') {
      const importe = reserva.importe_reserva || 0;
      return <Text fontSize="sm">{importe > 0 ? `${importe.toFixed(2)}€` : '-'}</Text>;
    }

    // Renderizar transaccion_adjudicacion (Order ID + Código Autorización)
    if (key === 'transaccion_adjudicacion') {
      const orderid = reserva.orderid_adjudicacion || 'N/A';
      const codigo = reserva.codigo_autorizacion_adjudicacion || 'N/A';
      
      return (
        <VStack spacing={1} align="stretch">
          <Text fontSize="xs" fontFamily="mono" color="gray.600">Order: {orderid}</Text>
          <Text fontSize="xs" fontFamily="mono" color="gray.600">Auth: {codigo}</Text>
        </VStack>
      );
    }

    // Renderizar importe_adjudicacion con formato de moneda
    if (key === 'importe_adjudicacion') {
      const importe = reserva.importe_adjudicacion || 0;
      return <Text fontSize="sm">{importe > 0 ? `${importe.toFixed(2)}€` : '-'}</Text>;
    }

    // Renderizar idioma con icono de bandera (ANTES de la verificación de null/undefined)
    if (key === 'idioma') {
      const idiomaLower = value ? value.toLowerCase() : 'es';
      const iconosDisponibles = ['es', 'en', 'de', 'fr', 'it', 'ru', 'ca'];
      const iconoPath = iconosDisponibles.includes(idiomaLower) 
        ? `/assets/icons/${idiomaLower}.png` 
        : '/assets/icons/es.png';
      
      return (
        <Box display="flex" justifyContent="center" alignItems="center">
          <img 
            src={iconoPath} 
            alt={idiomaLower} 
            style={{ width: '24px', height: '24px', objectFit: 'contain' }}
          />
        </Box>
      );
    }
    
    if (value === null || value === undefined) {
      return 'N/A';
    }

    // Renderizar estado con badge de color
    if (key === 'estado') {
      const color = getEstadoColor(value);
      return (
        <Badge colorScheme={color} fontSize="0.9em" px={3} py={1} borderRadius="full">
          {t(`estado_${value}`)}
        </Badge>
      );
    }

    if (key === 'flow_type') {
      const originType = String(value || 'web').toLowerCase();
      const colorByOrigin = {
        web: 'blue',
        chat: 'teal',
        telegram: 'purple',
        whatsapp: 'green',
      };
      const labelByOrigin = {
        web: 'Web',
        chat: 'Chat',
        telegram: 'Telegram',
        whatsapp: 'WhatsApp',
      };

      return (
        <Badge colorScheme={colorByOrigin[originType] || 'gray'} fontSize="0.85em" px={2} py={1} borderRadius="md">
          {labelByOrigin[originType] || originType}
        </Badge>
      );
    }

    // Renderizar sub_estado con badge de color
    if (key === 'sub_estado') {
      const color = getSubEstadoColor(value);
      return (
        <Badge 
          colorScheme={color} 
          fontSize="0.85em" 
          px={2} 
          py={1} 
          borderRadius="md"
          whiteSpace="normal"
          textAlign="center"
          display="inline-block"
          maxW="100%"
        >
          {t(`subestado_${value}`)}
        </Badge>
      );
    }

    // Formatear fechas
    if (key.includes('fecha') || key.includes('created_at') || key.includes('updated_at')) {
      try {
        const date = new Date(value);
        return date.toLocaleString('es-ES', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        });
      } catch {
        return String(value);
      }
    }

    // Formatear números decimales
    if (typeof value === 'number') {
      return value.toFixed(2);
    }

    return String(value);
  };

  const formatColumnHeader = (key) => {
    if (key === 'flow_type') return t('col_flow_type');
    return t(`col_${key}`);
  };

  if (loading && !isRefreshing) {
    return (
      <Box p={4} display="flex" justifyContent="center" alignItems="center" minH="200px" className="admin-loading">
        <Spinner size="xl" />
        <Text ml={4} className="admin-loading-text">{t('admin_reservas_cargando')}</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Alert status="error" className="admin-alert admin-alert-error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={4} borderWidth="1px" borderRadius="lg" overflow="auto" className="admin-page-container">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4} className="admin-page-header">
        <HStack spacing={2}>
          <Tooltip 
            label={autoRefreshEnabled ? "Desactivar auto refresh" : "Activar auto refresh"}
            placement="top"
            hasArrow
          >
            <Checkbox
              isChecked={autoRefreshEnabled}
              onChange={(e) => {
                const newValue = e.target.checked;
                setAutoRefreshEnabled(newValue);
                if (newValue) {
                  setRefreshCountdown(60);
                }
              }}
              colorScheme="teal"
              size="lg"
            />
          </Tooltip>
          <IconButton
            icon={<span style={{ fontSize: '18px' }}>&nbsp;🔄 ({refreshCountdown})&nbsp;</span>}
            onClick={() => {
              fetchReservas(true);
              setRefreshCountdown(60);
            }}
            colorScheme="teal"
            size="sm"
            variant="outline"
            aria-label="Refrescar reservas"
            title={`Refrescar lista de reservas (auto-refresco en ${refreshCountdown}s)`}
            isLoading={isRefreshing}
          />
          <Text fontSize="xl" fontWeight="bold" className="admin-page-title">
            {t('admin_reservas_titulo')}
          </Text>
        </HStack>
        <Button 
          onClick={() => setMostrarFiltros(!mostrarFiltros)}
          colorScheme="blue"
          size="sm"
          leftIcon={<span>🔍</span>}
          className="boton-filtros"
        >
          {t('filtros_boton')}
        </Button>
      </Box>

      {/* Leyenda de colores - Filtros interactivos */}
      {mostrarFiltros && (
      <Box className="filter-section-bg">
        <ReservaFilters
          estadosActivos={estadosActivos}
          subEstadosActivos={subEstadosActivos}
          toggleEstado={toggleEstado}
          toggleSubEstado={toggleSubEstado}
        />

        <Box mt={4}>
          <Text fontSize="sm" fontWeight="bold" mb={2} className="filter-section-title">{t('admin_reservas_filtro_origen_titulo')}</Text>
          <HStack spacing={2} wrap="wrap">
            <Badge
              cursor="pointer"
              colorScheme={origenesActivos.web ? 'blue' : 'gray'}
              px={3}
              py={1}
              borderRadius="md"
              onClick={() => toggleOrigen('web')}
            >
              {t('admin_reservas_origen_web')}
            </Badge>
            <Badge
              cursor="pointer"
              colorScheme={origenesActivos.chat ? 'teal' : 'gray'}
              px={3}
              py={1}
              borderRadius="md"
              onClick={() => toggleOrigen('chat')}
            >
              {t('admin_reservas_origen_chat')}
            </Badge>
            <Badge
              cursor="pointer"
              colorScheme={origenesActivos.telegram ? 'purple' : 'gray'}
              px={3}
              py={1}
              borderRadius="md"
              onClick={() => toggleOrigen('telegram')}
            >
              {t('admin_reservas_origen_telegram')}
            </Badge>
            <Badge
              cursor="pointer"
              colorScheme={origenesActivos.whatsapp ? 'green' : 'gray'}
              px={3}
              py={1}
              borderRadius="md"
              onClick={() => toggleOrigen('whatsapp')}
            >
              {t('admin_reservas_origen_whatsapp')}
            </Badge>
          </HStack>
        </Box>
      </Box>
      )}

      {reservas.length > 0 ? (
        <Table variant="simple" width="100%" minWidth="1200px" className="admin-table">
          <Thead>
            <Tr>
              {columnsToDisplay.map((key) => (
                <Th
                  key={key}
                  cursor="pointer"
                  onClick={() => handleSort(key)}
                  maxW={key === 'transaccion_reserva' || key === 'transaccion_adjudicacion' ? '120px' : undefined}
                  whiteSpace={key === 'transaccion_reserva' || key === 'transaccion_adjudicacion' ? 'normal' : undefined}
                >
                  {formatColumnHeader(key)}
                  {sortBy === key && (sortOrder === 'asc' ? ' ⬆️' : ' ⬇️')}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {reservas.map((reserva) => (
              <Tr
                key={reserva.id}
                _hover={{ backgroundColor: 'rgba(0, 0, 0, 0.03)', cursor: 'pointer' }}
              >
                {columnsToDisplay.map((key) => (
                  <Td 
                    key={`${reserva.id}-${key}`}
                    maxW={key === 'sub_estado' || key === 'transaccion_reserva' || key === 'transaccion_adjudicacion' ? '120px' : undefined}
                    whiteSpace={key === 'sub_estado' || key === 'transaccion_reserva' || key === 'transaccion_adjudicacion' ? 'normal' : undefined}
                  >
                    {renderCellValue(reserva, key)}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      ) : (
        <Text>No hay reservas registradas.</Text>
      )}
    </Box>
  );
};

export default AdminReservas;
